import React, { useState, useRef, useEffect } from 'react';
import { X, Hash, Loader2, Image as ImageIcon } from 'lucide-react';
import { useCreateDiscussion, useEditDiscussion } from '../hooks/use-panels';
import { uploadFile, confirmUpload } from '../../../lib/uploads';

const CreateDiscussionModal = ({ isOpen, onClose, panelId, initialData }) => {
  const [form, setForm] = useState({
    title: initialData?.title || '',
    content: initialData?.content || '',
    tagInput: '',
    tags: initialData?.tags || [],
    // In edit mode the API returns signed URLs in `images` and the raw storage
    // keys in `imageKeys`. Submitting signed URLs would fail key validation, so
    // the form carries keys and shows previews separately.
    images: initialData?.imageKeys || (initialData?.imageUrlKey ? [initialData.imageUrlKey] : []),
    previews: initialData?.images || (initialData?.imageUrl ? [initialData.imageUrl] : []),
    // Uploads in progress or finished, one per newly chosen file:
    // { file, key, uploading, progress, failed }. Each starts uploading as soon
    // as it is picked, so `key` fills in while the rest of the form is written.
    pendingFiles: [],
  });
  const [error, setError] = useState('');
  const [showImageTooltip, setShowImageTooltip] = useState(false);
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);

  // Auto-resize title textarea as text is added or removed
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = `${Math.max(42, titleRef.current.scrollHeight)}px`;
    }
  }, [form.title, isOpen]);

  // Auto-resize content textarea as text is added or removed
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.height = 'auto';
      contentRef.current.style.height = `${Math.max(180, contentRef.current.scrollHeight)}px`;
    }
  }, [form.content, isOpen]);

  const isUploading = (form.pendingFiles || []).some((p) => p.uploading);

  const { mutate: createDiscussion, isPending: isCreating } = useCreateDiscussion();
  const { mutate: editDiscussion, isPending: isEditing } = useEditDiscussion();

  // Spans the whole submit, not just the mutation. Promoting the images out of
  // `tmp/` is a round-trip per image before the mutation even starts, so the
  // mutations' own isPending leaves the button silent through all of it.
  const [isPosting, setIsPosting] = useState(false);
  // True from the moment Post is pressed until the row is saved or has failed.
  const isSaving = isPosting || isCreating || isEditing;
  const isPending = isSaving || isUploading;
  const isEditMode = !!initialData;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = form.tagInput.trim().replace(/^#/, '');
      if (tag && !form.tags.includes(tag)) {
        setForm((prev) => ({ ...prev, tags: [...prev.tags, tag], tagInput: '' }));
      }
    }
  };

  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const removeImage = (indexToRemove) => {
    setForm((prev) => {
      // previews is [...already-stored images, ...newly picked files], so an
      // index past the stored ones refers to a pending file.
      const storedCount = prev.images.length;
      const url = (prev.previews || [])[indexToRemove];
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== indexToRemove),
        pendingFiles: (prev.pendingFiles || []).filter(
          (_, i) => i + storedCount !== indexToRemove
        ),
        previews: (prev.previews || []).filter((_, i) => i !== indexToRemove),
      };
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (form.images.length + (form.pendingFiles?.length || 0) + files.length > 2) {
      setError('You can upload a maximum of 2 images.');
      return;
    }

    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        setError('Please select only image files (JPG, PNG, GIF)');
        return;
      }
      // Enforce 1MB limit per image
      if (file.size > 1 * 1024 * 1024) {
        setError('Image must be under 1MB.');
        return;
      }
    }

    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Show each file immediately from a local blob:, then upload it in the
    // background. Until an upload is confirmed the object lives only in a
    // staging prefix the bucket sweeps, so abandoning the form costs nothing
    // that lasts.
    setForm((prev) => ({
      ...prev,
      pendingFiles: [
        ...(prev.pendingFiles || []),
        ...files.map((file) => ({ file, key: null, uploading: true, progress: 0, failed: false })),
      ],
      previews: [...(prev.previews || []), ...files.map((f) => URL.createObjectURL(f))],
    }));

    // Patch by identity, not index: several uploads finish in any order, and
    // removing a thumbnail mid-flight shifts every index after it.
    const patch = (file, changes) =>
      setForm((prev) => ({
        ...prev,
        pendingFiles: (prev.pendingFiles || []).map((p) =>
          p.file === file ? { ...p, ...changes } : p
        ),
      }));

    await Promise.all(
      files.map(async (file) => {
        try {
          const result = await uploadFile(file, {
            purpose: 'discussion-image',
            onProgress: (progress) => patch(file, { progress }),
          });
          patch(file, { key: result.key, uploading: false, progress: 100 });
        } catch (err) {
          console.error('Upload error:', err);
          patch(file, { uploading: false, failed: true });
          setError(
            err?.response?.data?.message || 'An image failed to upload. Please remove it and try again.'
          );
        }
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required.');
      return;
    }

    // The images are already in B2 by now — only wait if any are still going.
    if (isUploading) {
      setError('Please wait for the images to finish uploading.');
      return;
    }
    if ((form.pendingFiles || []).some((p) => p.failed)) {
      setError('An image failed to upload. Please remove it and try again.');
      return;
    }

    // Promote the staged uploads. Every image sits under `tmp/` until this
    // point, where the bucket lifecycle rule sweeps it — so abandoning this form
    // leaves nothing stranded at a permanent key. See lib/uploads.js.
    //
    // This is also where a file whose bytes do not match its declared type is
    // rejected, so a failure here must stop the submit: the object is deleted
    // server-side and must not be referenced.
    const staged = (form.pendingFiles || []).filter((p) => p.key);
    let promoted;
    setIsPosting(true);
    try {
      promoted = await Promise.all(
        staged.map(async (p) => ({
          ...p,
          key: (await confirmUpload(p.key, 'discussion-image')).key,
        }))
      );
    } catch (err) {
      console.error('Confirm error:', err);
      // Split the same way putToStorage splits its retries. A 4xx is the file
      // itself being refused — wrong type, too large, not this user's — and
      // removing it is the real fix. Anything else (5xx, or no response at all
      // when the gateway has no upstream mid-deploy) is transient: the image is
      // valid and still staged, so telling the user to remove it would throw
      // away a good upload to fix a problem it did not cause.
      const status = err?.response?.status;
      setError(
        status && status < 500
          ? err?.response?.data?.message ||
          'An image could not be saved. Please remove it and try again.'
          : 'Could not reach the server. Your images are still attached — please try again.'
      );
      setIsPosting(false);
      return;
    }

    // Write the permanent keys back, so a retry after a failed save does not try
    // to confirm the same staging key a second time.
    setForm((prev) => ({
      ...prev,
      pendingFiles: (prev.pendingFiles || []).map(
        (p) => promoted.find((q) => q.file === p.file) ?? p
      ),
    }));

    // form.images holds the keys kept from a previous save (edit mode); the
    // promoted slots hold the newly confirmed permanent keys.
    const imageKeys = [...form.images, ...promoted.map((p) => p.key)];

    const extractedTags = (form.content.match(/#[\w-]+/g) || []).map(t => t.slice(1));
    const pendingTag = form.tagInput.trim().replace(/^#/, '');
    const allTags = [...new Set([...form.tags, ...extractedTags, ...(pendingTag ? [pendingTag] : [])])];

    const payload = {
      panelId,
      title: form.title,
      content: form.content,
      tags: allTags,
      // On edit, always send the array. The server reads `undefined` as "leave
      // images untouched" and `[]` as "remove them all" — collapsing the two
      // made removing the last image a silent no-op.
      images: isEditMode ? imageKeys : (imageKeys.length > 0 ? imageKeys : undefined)
    };
    const options = {
      onSuccess: () => {
        setIsPosting(false);
        setForm({ title: '', content: '', tagInput: '', tags: [], images: [], pendingFiles: [], previews: [] });
        onClose();
      },
      onError: (err) => {
        setIsPosting(false);
        setError(err?.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} discussion.`);
      },
    };

    if (isEditMode) {
      editDiscussion({ ...payload, discussionId: initialData.id }, options);
    } else {
      createDiscussion(payload, options);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {isEditMode ? 'Edit Discussion' : 'Start a Discussion'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditMode ? 'Update your post details' : 'Share your thoughts with the panel'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Title <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs ${form.title.length >= 300 ? 'text-red-500 font-bold' : form.title.length >= 270 ? 'text-amber-500 font-semibold' : 'text-gray-400'}`}>
                {form.title.length} / 300
              </span>
            </div>
            <textarea
              ref={titleRef}
              rows={1}
              value={form.title}
              maxLength={300}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Protocol Update: Rapid Assessment for Acute Abdomen (max 300 characters)"
              className="w-full px-4 py-2.5 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors break-words [overflow-wrap:anywhere] overflow-y-hidden resize-none min-h-[42px]"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Content <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs ${form.content.length >= 5000 ? 'text-red-500 font-bold' : form.content.length >= 4500 ? 'text-amber-500 font-semibold' : 'text-gray-400'}`}>
                {form.content.length} / 5000 characters
              </span>
            </div>
            <textarea
              ref={contentRef}
              value={form.content}
              maxLength={5000}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Share your insights, questions, or case discussions..."
              rows={8}
              className="w-full px-4 py-3 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors break-words [overflow-wrap:anywhere] overflow-y-hidden resize-none min-h-[180px]"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Images
              </label>
              <span className="text-xs text-gray-400">
                {form.images.length + (form.pendingFiles?.length || 0)}/2 uploaded
              </span>
            </div>

            {/* Image Guidelines Card (Case Share Style) */}
            <div className="flex items-start gap-2.5 p-3 bg-indigo-50/60 border border-indigo-100/80 rounded-xl text-xs text-indigo-900 mb-3">
              <ImageIcon size={15} className="text-indigo-600 shrink-0 mt-0.5" />
              <div className="space-y-0.5 text-[11px] leading-relaxed">
                <span className="font-semibold text-indigo-950 block text-xs">Image Upload Guidelines</span>
                <p className="text-gray-600">Supports JPG, PNG, GIF, WebP • Max 1 MB per image • Up to 2 images</p>
              </div>
            </div>

            {/* Gate on previews, not images: a file picked but not yet uploaded
                has a preview and no key, so keying off images hides it. */}
            {form.previews && form.previews.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3">
                {(form.previews || []).map((url, index) => {
                  // previews is [...already-stored, ...newly picked], so anything
                  // past the stored ones maps to a pending upload slot.
                  const slot = (form.pendingFiles || [])[index - form.images.length];
                  return (
                    <div key={url} className="relative inline-block">
                      <img
                        src={url}
                        alt={`Upload preview ${index + 1}`}
                        className={`h-24 w-24 sm:h-32 sm:w-32 rounded-lg object-cover border ${slot?.failed ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {slot?.uploading && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg flex flex-col items-center justify-center text-white">
                          <Loader2 className="animate-spin" size={18} />
                          <span className="text-[11px] font-medium mt-1">{slot.progress}%</span>
                        </div>
                      )}
                      {slot?.failed && (
                        <div className="absolute inset-0 bg-red-900/50 rounded-lg flex items-center justify-center">
                          <span className="text-[11px] font-semibold text-white px-1 text-center">Upload failed</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 p-1 bg-white border border-gray-200 rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50 shadow-sm"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*"
              multiple
            />
            <div className="relative inline-block">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onMouseEnter={() => setShowImageTooltip(true)}
                onMouseLeave={() => setShowImageTooltip(false)}
                disabled={isUploading || form.images.length + (form.pendingFiles?.length || 0) >= 2}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <ImageIcon size={14} className="text-indigo-600" />
                <span>+Add Images</span>
                {form.images.length + (form.pendingFiles?.length || 0) > 0 && (
                  <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-full font-bold">
                    {form.images.length + (form.pendingFiles?.length || 0)}/2
                  </span>
                )}
              </button>

              {/* Tooltip popover */}
              {showImageTooltip && (
                <div className="absolute left-0 bottom-full mb-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 pointer-events-none border border-gray-700/60 animate-in fade-in zoom-in-95">
                  <div className="flex items-center gap-1.5 font-bold text-indigo-300 text-xs border-b border-gray-700 pb-1">
                    <ImageIcon size={13} />
                    <span>Allowed Image Formats</span>
                  </div>
                  <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                    <p><span className="font-semibold text-white">• Supported: </span>JPG, JPEG, PNG, WebP, GIF</p>
                    <p><span className="font-semibold text-white">• Limit: </span>Max 2 images</p>
                    <p><span className="font-semibold text-white">• Max Size: </span>1 MB per image</p>
                  </div>
                  <div className="mt-1 pt-1 border-t border-gray-700 text-gray-300 text-[10px] font-medium flex items-center justify-between">
                    <span>Attached: {form.images.length + (form.pendingFiles?.length || 0)}/2</span>
                    {form.images.length + (form.pendingFiles?.length || 0) >= 2 && (
                      <span className="text-amber-400 font-semibold">Max reached</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tags <span className="text-xs text-gray-400">(press Enter to add)</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-full"
                >
                  <Hash size={11} />
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-blue-800">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={form.tagInput}
              onChange={(e) => handleChange('tagInput', e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="#FAST ultrasound, #septic shock"
              className="w-full px-4 py-2.5 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-lg">{error}</p>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="w-full py-3 text-sm font-semibold text-white bg-[#6b46c1] rounded-xl hover:bg-[#553098] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {/* Spins for the whole submit — promoting the images out of `tmp/`
                and then saving the row — but NOT for the uploads. `isPending`
                folds in `isUploading`, and using it here would put a second
                spinner on screen beside the thumbnails' own, labelled
                "Saving..." before any save has begun. Disabling still uses
                `isPending`, so the form cannot be submitted mid-upload. */}
            {isSaving ? (
              <><Loader2 size={16} className="animate-spin" /> {isEditMode ? 'Saving...' : 'Posting...'}</>
            ) : (
              isEditMode ? 'Save Changes' : 'Post Discussion'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateDiscussionModal;
